import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { TooltipProvider } from '../../components/ui/tooltip';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import TimetableGrid from '../../components/timetable/TimetableGrid.jsx';
import DaySchedule from '../../components/timetable/DaySchedule.jsx';
import { useTimetable } from '../../hooks/useTimetable.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { Calendar, Download, Printer, RefreshCw, Clock } from 'lucide-react';

const TimetablePage = () => {
  const { user } = useAuth();
  const {
    entries,
    loading,
    error,
    viewMode,
    setViewMode,
    selectedDay,
    setSelectedDay,
    exportTimetable,
    printTimetable,
    getUpcomingClasses,
    refetch
  } = useTimetable();

  const upcomingClasses = getUpcomingClasses();

  const getRoleSpecificTitle = () => {
    switch (user?.role) {
      case 'student':
        return 'My Timetable';
      case 'teacher':
        return 'Teaching Schedule';
      case 'parent':
        return 'Child\'s Timetable';
      case 'staff':
        return 'Schedule';
      default:
        return 'Timetable';
    }
  };

  const getRoleSpecificDescription = () => {
    switch (user?.role) {
      case 'student':
        return 'Your class schedule for this term';
      case 'teacher':
        return 'Your teaching schedule across all classes';
      case 'parent':
        return 'Your child\'s class schedule';
      case 'staff':
        return 'Your work schedule';
      default:
        return 'Schedule information';
    }
  };

  if (loading) return <LoadingSpinner />;
  
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={refetch}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <TooltipProvider>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{getRoleSpecificTitle()}</h2>
          <p className="text-gray-600">{getRoleSpecificDescription()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => exportTimetable('pdf')}
            disabled={!entries.length}
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={printTimetable}
            disabled={!entries.length}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Upcoming Classes */}
      {upcomingClasses.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold">Upcoming Classes</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {upcomingClasses.slice(0, 3).map((entry, index) => (
                <Badge key={index} variant="outline" className="text-sm">
                  {entry.dayOfWeek} {entry.startTime} - {entry.subjectName}
                </Badge>
              ))}
              {upcomingClasses.length > 3 && (
                <Badge variant="outline" className="text-sm">
                  +{upcomingClasses.length - 3} more
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {(!entries || entries.length === 0) ? (
        <EmptyState 
          icon={Calendar}
          title="No timetable entries"
          description="Your timetable will appear here once it's been set up."
          action
          actionLabel="Refresh"
          onAction={refetch}
        />
      ) : (
        <div className="space-y-6">
          {/* Day Selector for Daily View */}
          {viewMode === 'daily' && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold">Select Day</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                    <Button
                      key={day}
                      variant={selectedDay === day ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedDay(selectedDay === day ? null : day)}
                    >
                      {day}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timetable Display */}
          {viewMode === 'daily' && selectedDay ? (
            <DaySchedule 
              entries={entries.filter(entry => entry.dayOfWeek === selectedDay)}
              day={selectedDay}
              userRole={user?.role}
            />
          ) : (
            <TimetableGrid
              entries={entries}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              onExport={() => exportTimetable('pdf')}
              onPrint={printTimetable}
              userRole={user?.role}
            />
          )}
        </div>
      )}
      </div>
    </TooltipProvider>
  );
};

export default TimetablePage;


